import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const ContractList = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const navigate = useNavigate();
  const isRTL = lang === 'ar';

  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [clients, setClients] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/contracts/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (data.success) setStats(data.data); }
    } catch (err) { console.error('Stats error:', err); }
  }, []);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (clientFilter) params.append('client_id', clientFilter);
      const res = await fetch(`${API}/contracts?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setContracts(data.data || []);
          setTotalPages(data.pagination?.totalPages || 1);
          setTotal(data.pagination?.total || 0);
        }
      }
    } catch (err) { console.error('Contracts error:', err); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, typeFilter, clientFilter]);

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/clients?limit=200`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (data.success) setClients(data.data || []); }
    } catch (err) { console.error('Clients error:', err); }
  }, []);

  useEffect(() => { fetchStats(); fetchClients(); }, []);
  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  const fmtCurrency = (v) => {
    if (!v && v !== 0) return '—';
    return Number(v).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US');
  };

  const statusColors = {
    draft: { bg: 'rgba(107,114,128,0.2)', color: '#9ca3af' },
    active: { bg: 'rgba(22,163,106,0.2)', color: '#4ade80' },
    suspended: { bg: 'rgba(217,119,6,0.2)', color: '#fbbf24' },
    expired: { bg: 'rgba(220,38,38,0.2)', color: '#f87171' },
    cancelled: { bg: 'rgba(107,114,128,0.15)', color: '#6b7280' },
    renewed: { bg: 'rgba(37,99,235,0.2)', color: '#60a5fa' },
  };

  const statusLabel = (s) => f[`cst_${s}`] || s;
  const typeLabel = (t_) => f[`ctp_${t_}`] || t_;

  const inputSt = {
    padding: '10px 14px', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const statCards = stats ? [
    { label: f.ct_active_contracts || 'Active Contracts', value: stats.active_count || 0, icon: '📋', color: '#4ade80' },
    { label: f.ct_expiring_soon || 'Expiring Soon', value: stats.expiring_count || 0, icon: '⏰', color: '#fbbf24' },
    { label: f.ct_expired || 'Expired', value: stats.expired_count || 0, icon: '❌', color: '#f87171' },
    { label: f.ct_total_value || 'Total Value', value: `${fmtCurrency(stats.total_value)}`, icon: '💰', color: '#60a5fa' },
    { label: f.ct_attach_rate || 'Attach Rate', value: `${stats.attach_rate || 0}%`, icon: '🔗', color: '#a78bfa' },
  ] : [];

  return (
    <Layout>
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#f9fafb' }}>{f.contracts_title || 'Service Contracts'}</h1>
          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '14px' }}>{f.contracts_subtitle || 'Manage service contracts and SLA agreements'}</p>
        </div>
        <button onClick={() => navigate('/fsm/contracts/new')} style={{
          padding: '10px 20px', borderRadius: '8px', border: 'none',
          backgroundColor: '#007bff', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px'
        }}>+ {f.new_contract || 'New Contract'}</button>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {statCards.map((card, i) => (
            <div key={i} style={{ ...sectionSt, padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: '500' }}>{card.label}</span>
                <span style={{ fontSize: '20px' }}>{card.icon}</span>
              </div>
              <span style={{ fontSize: '26px', fontWeight: '700', color: card.color, display: 'block', marginTop: '8px' }}>{card.value}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...sectionSt, padding: '16px', marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder={f.search_contracts || 'Search contracts...'} value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ ...inputSt, flex: '1 1 200px' }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...inputSt, minWidth: '140px' }}>
          <option value="">{f.all_statuses || 'All Statuses'}</option>
          {['draft', 'active', 'suspended', 'expired', 'cancelled', 'renewed'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{ ...inputSt, minWidth: '140px' }}>
          <option value="">{f.all_types || 'All Types'}</option>
          {['warranty', 'maintenance', 'full_service', 'on_demand', 'custom'].map(tp => <option key={tp} value={tp}>{typeLabel(tp)}</option>)}
        </select>
        <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setPage(1); }} style={{ ...inputSt, minWidth: '160px' }}>
          <option value="">{f.all_clients || 'All Clients'}</option>
          {clients.map(c => <option key={c.id} value={c.id}>{lang === 'ar' ? (c.name_ar || c.name) : c.name}</option>)}
        </select>
      </div>

      <div style={{ ...sectionSt, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>{f.loading || 'Loading...'}</div>
        ) : contracts.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
            <p>{f.no_contracts || 'No contracts found'}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  {[f.ct_number || 'Contract #', f.ct_client || 'Client', f.ct_type || 'Type', f.ct_status || 'Status',
                    f.ct_start || 'Start', f.ct_end || 'End', f.ct_value || 'Value', f.ct_equipment_count || 'Equipment'
                  ].map((h, i) => (
                    <th key={i} style={{ padding: '12px 16px', textAlign: isRTL ? 'right' : 'left', fontWeight: '600', color: '#d1d5db', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contracts.map(c => {
                  const sc = statusColors[c.status] || statusColors.draft;
                  return (
                    <tr key={c.id} onClick={() => navigate(`/fsm/contracts/${c.id}`)}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={{ padding: '12px 16px', fontWeight: '600', color: '#60a5fa', whiteSpace: 'nowrap' }}>{c.contract_number}</td>
                      <td style={{ padding: '12px 16px', color: '#f9fafb' }}>{lang === 'ar' ? (c.client_name_ar || c.client_name) : c.client_name}</td>
                      <td style={{ padding: '12px 16px', color: '#d1d5db' }}>{typeLabel(c.contract_type || c.type)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', backgroundColor: sc.bg, color: sc.color }}>{statusLabel(c.status)}</span>
                      </td>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: '#d1d5db' }}>{fmtDate(c.start_date)}</td>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: '#d1d5db' }}>{fmtDate(c.end_date)}</td>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontWeight: '600', color: '#f9fafb' }}>{fmtCurrency(c.contract_value)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#d1d5db' }}>{c.equipment_count || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '13px', color: '#9ca3af' }}>
            <span>{lang === 'ar' ? `عرض ${contracts.length} من ${total}` : `Showing ${contracts.length} of ${total}`}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{
                padding: '6px 12px', borderRadius: '6px', border: '2px solid rgba(255,255,255,0.15)',
                backgroundColor: 'transparent', cursor: page <= 1 ? 'default' : 'pointer', color: page <= 1 ? '#6b7280' : '#d1d5db'
              }}>{isRTL ? '→' : '←'}</button>
              <span style={{ padding: '6px 12px', fontWeight: '600', color: '#f9fafb' }}>{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{
                padding: '6px 12px', borderRadius: '6px', border: '2px solid rgba(255,255,255,0.15)',
                backgroundColor: 'transparent', cursor: page >= totalPages ? 'default' : 'pointer', color: page >= totalPages ? '#6b7280' : '#d1d5db'
              }}>{isRTL ? '←' : '→'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default ContractList;